import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, FileText, Users, Euro, Clock, Send, ChevronRight,
  CheckCircle2, ArrowRight, ArrowLeft, Mail, Phone, Building2, Home,
  Calendar, Download, Printer, Shield, Search, Filter, Eye, Check
} from 'lucide-react';
import { apiClient } from '@/App';
import { toast } from 'sonner';

const GestionImpayes = ({ user }) => {
  const navigate = useNavigate();
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [appelsFonds, setAppelsFonds] = useState([]);
  const [copropriete, setCopropriete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDebiteur, setSelectedDebiteur] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, impayes, relances
  
  const [formData, setFormData] = useState({
    syndic_type: 'benevole',
    syndic_nom: user?.nom || '',
    syndic_prenom: user?.prenom || '',
    syndic_email: user?.email || '',
    syndic_telephone: user?.telephone || '',
    copropriete_nom: '',
    copropriete_adresse: '',
    debiteur_civilite: 'Madame, Monsieur',
    debiteur_nom: '',
    debiteur_prenom: '',
    debiteur_adresse: '',
    debiteur_email: '',
    montant_principal: '',
    date_echeance: '',
    nature_charges: 'charges_courantes',
    delai_paiement: '15',
    mention_interets: true,
    mention_judiciaire: true,
  });
  
  const [generatedLetter, setGeneratedLetter] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [coproRes, appelsRes] = await Promise.all([
        apiClient.get('/coproprietaires'),
        apiClient.get('/appels-fonds')
      ]);
      
      setCoproprietaires(coproRes.data || []);
      setAppelsFonds(appelsRes.data || []);
      
      // Récupérer les infos de la copropriété depuis le user
      if (user?.copropriete_active_id) {
        try {
          const coproprieteRes = await apiClient.get(`/coproprietes/${user.copropriete_active_id}`);
          setCopropriete(coproprieteRes.data);
          setFormData(prev => ({
            ...prev,
            copropriete_nom: coproprieteRes.data?.nom || '',
            copropriete_adresse: coproprieteRes.data?.adresse || '',
          }));
        } catch (e) {
          console.log('Copropriété info not available');
        }
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Calculer les impayés par copropriétaire
  const getImpayesParCoproprietaire = () => {
    const impayes = {};
    
    appelsFonds.forEach(appel => {
      if (appel.paiements) {
        Object.entries(appel.paiements).forEach(([coproId, paiement]) => {
          if (paiement.statut !== 'payé' && paiement.statut !== 'paye') {
            if (!impayes[coproId]) {
              impayes[coproId] = {
                total: 0,
                appels: [],
                derniere_echeance: null
              };
            }
            impayes[coproId].total += paiement.montant_du || 0;
            impayes[coproId].appels.push({
              id: appel.id,
              description: appel.description,
              montant: paiement.montant_du,
              date_echeance: appel.date_echeance,
              relance_niveau: paiement.relance_niveau || 0
            });
            
            // Garder la dernière échéance
            if (!impayes[coproId].derniere_echeance || appel.date_echeance > impayes[coproId].derniere_echeance) {
              impayes[coproId].derniere_echeance = appel.date_echeance;
            }
          }
        });
      }
    });
    
    return impayes;
  };

  const impayesParCopro = getImpayesParCoproprietaire();

  // Filtrer les copropriétaires avec impayés
  const coproprietairesAvecImpayes = coproprietaires
    .filter(c => impayesParCopro[c.id])
    .map(c => ({
      ...c,
      impayes: impayesParCopro[c.id]
    }))
    .filter(c => {
      // Filtre de recherche
      const matchSearch = !searchTerm || 
        `${c.prenom} ${c.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre de statut
      if (filterStatus === 'all') return matchSearch;
      if (filterStatus === 'impayes') return matchSearch && c.impayes.total > 0;
      if (filterStatus === 'relances') return matchSearch && c.impayes.appels.some(a => a.relance_niveau > 0);
      
      return matchSearch;
    })
    .sort((a, b) => b.impayes.total - a.impayes.total);

  const startMiseEnDemeure = (coproprietaire) => {
    setSelectedDebiteur(coproprietaire);
    setFormData(prev => ({
      ...prev,
      debiteur_nom: coproprietaire.nom || '',
      debiteur_prenom: coproprietaire.prenom || '',
      debiteur_email: coproprietaire.email || '',
      debiteur_adresse: copropriete?.adresse || '',
      montant_principal: coproprietaire.impayes.total.toString(),
      date_echeance: coproprietaire.impayes.derniere_echeance?.split('T')[0] || '',
    }));
    setShowGenerator(true);
    setCurrentStep(0);
  };

  const generateLetter = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    
    const letter = `
${formData.copropriete_nom}
Syndicat des copropriétaires
${formData.copropriete_adresse}

${formData.syndic_prenom} ${formData.syndic_nom}
Syndic bénévole
${formData.syndic_email ? `Email : ${formData.syndic_email}` : ''}
${formData.syndic_telephone ? `Tél : ${formData.syndic_telephone}` : ''}

Le ${formattedDate}

${formData.debiteur_civilite} ${formData.debiteur_prenom} ${formData.debiteur_nom}
${formData.debiteur_adresse}

Lettre recommandée avec accusé de réception

Objet : MISE EN DEMEURE DE PAYER – Charges de copropriété impayées

${formData.debiteur_civilite} ${formData.debiteur_nom},

En ma qualité de syndic bénévole de la copropriété « ${formData.copropriete_nom} » sise ${formData.copropriete_adresse}, je me vois dans l'obligation de vous adresser la présente mise en demeure.

En effet, malgré les appels de fonds qui vous ont été adressés, je constate que vous restez redevable envers le syndicat des copropriétaires de la somme de :

    ${parseFloat(formData.montant_principal).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
    
au titre des ${formData.nature_charges === 'charges_courantes' ? 'charges courantes de copropriété' : formData.nature_charges === 'travaux' ? 'travaux votés en assemblée générale' : 'charges courantes et travaux votés en assemblée générale'}, dont l'échéance était fixée au ${new Date(formData.date_echeance).toLocaleDateString('fr-FR')}.

Par la présente, je vous mets en demeure de procéder au règlement intégral de cette somme dans un délai de ${formData.delai_paiement} jours à compter de la réception de ce courrier.

${formData.mention_interets ? `Conformément à l'article 1231-6 du Code civil, des intérêts de retard au taux légal sont dus de plein droit à compter de la présente mise en demeure.

` : ''}${formData.mention_judiciaire ? `À défaut de paiement dans le délai imparti, je me verrai contraint d'engager une procédure judiciaire à votre encontre (injonction de payer ou assignation devant le tribunal), dont les frais seront mis à votre charge.

Je vous rappelle que le syndicat des copropriétaires bénéficie d'un privilège spécial immobilier (article 2374 du Code civil) garantissant le recouvrement des charges sur votre lot en cas de vente.

` : ''}Je reste néanmoins à votre disposition pour examiner toute proposition de règlement amiable, notamment la mise en place d'un échéancier.

Veuillez agréer, ${formData.debiteur_civilite} ${formData.debiteur_nom}, l'expression de mes salutations distinguées.

${formData.syndic_prenom} ${formData.syndic_nom}
Syndic de la copropriété
    `.trim();
    
    setGeneratedLetter(letter);
  };

  const handleClearbusCheckout = async () => {
    try {
      const response = await apiClient.post('/services/mise-en-demeure/checkout', {
        coproprietaire_id: selectedDebiteur?.id,
        montant: parseFloat(formData.montant_principal) || 0,
        debiteur_email: formData.debiteur_email
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Erreur checkout Clearbus:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
    }
  };

  const steps = [
    { id: 'details', title: 'Vérifier les détails' },
    { id: 'options', title: 'Options' },
    { id: 'preview', title: 'Aperçu' },
    { id: 'envoi', title: 'Envoi' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  // Vue Générateur de mise en demeure
  if (showGenerator && selectedDebiteur) {
    return (
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => { setShowGenerator(false); setCurrentStep(0); setGeneratedLetter(null); }}
            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à la liste
          </button>
          <h1 className="text-xl font-bold text-slate-900">Mise en demeure</h1>
          <div className="w-24" />
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                currentStep > index ? 'bg-emerald-500 text-white' :
                currentStep === index ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' :
                'bg-slate-200 text-slate-500'
              }`}>
                {currentStep > index ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 sm:w-24 h-1 mx-2 ${currentStep > index ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Étape 0: Vérifier les détails */}
        {currentStep === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Vérifier les informations</h2>
            
            <div className="bg-red-50 rounded-xl p-4 border border-red-100">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <span className="font-semibold text-red-700">Débiteur : {selectedDebiteur.prenom} {selectedDebiteur.nom}</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{selectedDebiteur.impayes.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € impayés</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={formData.debiteur_prenom}
                  onChange={(e) => setFormData(prev => ({ ...prev, debiteur_prenom: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={formData.debiteur_nom}
                  onChange={(e) => setFormData(prev => ({ ...prev, debiteur_nom: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
              <input
                type="text"
                value={formData.debiteur_adresse}
                onChange={(e) => setFormData(prev => ({ ...prev, debiteur_adresse: e.target.value }))}
                placeholder="Appartement X, Résidence..."
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email (pour Clearbus)</label>
              <input
                type="email"
                value={formData.debiteur_email}
                onChange={(e) => setFormData(prev => ({ ...prev, debiteur_email: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Montant réclamé (€)</label>
                <input
                  type="number"
                  value={formData.montant_principal}
                  onChange={(e) => setFormData(prev => ({ ...prev, montant_principal: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date d'échéance</label>
                <input
                  type="date"
                  value={formData.date_echeance}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_echeance: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              </div>
            </div>
          </div>
        )}

        {/* Étape 1: Options */}
        {currentStep === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Options de la mise en demeure</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Délai accordé pour le paiement</label>
              <div className="flex gap-3">
                {['8', '15', '30'].map((days) => (
                  <button
                    key={days}
                    onClick={() => setFormData(prev => ({ ...prev, delai_paiement: days }))}
                    className={`flex-1 py-3 rounded-xl border-2 font-semibold transition-all ${
                      formData.delai_paiement === days
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                        : 'border-slate-200 text-slate-600 hover:border-emerald-300'
                    }`}
                  >
                    {days} jours
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mention_interets}
                  onChange={(e) => setFormData(prev => ({ ...prev, mention_interets: e.target.checked }))}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-medium text-slate-900">Mention des intérêts de retard</p>
                  <p className="text-sm text-slate-500">Rappel des intérêts légaux</p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-emerald-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.mention_judiciaire}
                  onChange={(e) => setFormData(prev => ({ ...prev, mention_judiciaire: e.target.checked }))}
                  className="mt-1 w-5 h-5 rounded border-slate-300 text-emerald-500 focus:ring-emerald-500"
                />
                <div>
                  <p className="font-medium text-slate-900">Avertissement de poursuites judiciaires</p>
                  <p className="text-sm text-slate-500">Mention de l'injonction de payer et du privilège</p>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Étape 2: Aperçu */}
        {currentStep === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-6">
            <h2 className="text-lg font-bold text-slate-900">Aperçu de la lettre</h2>
            
            {!generatedLetter && generateLetter()}
            
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-96 overflow-y-auto">
              <pre className="whitespace-pre-wrap font-serif text-sm text-slate-800 leading-relaxed">
                {generatedLetter}
              </pre>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  const blob = new Blob([generatedLetter], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `mise_en_demeure_${formData.debiteur_nom}_${new Date().toISOString().split('T')[0]}.txt`;
                  a.click();
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              >
                <Download className="w-5 h-5" />
                Télécharger
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              >
                <Printer className="w-5 h-5" />
                Imprimer
              </button>
            </div>
          </div>
        )}

        {/* Étape 3: Envoi */}
        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 space-y-5">
            <h2 className="text-lg font-bold text-slate-900">Envoyer la mise en demeure</h2>
            
            {/* Obligation légale */}
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-red-800 mb-1">⚖️ Obligation légale</h3>
                  <p className="text-red-700 text-sm">
                    L'article 19 de la loi du 10 juillet 1965 impose l'envoi d'une <strong>mise en demeure par lettre recommandée avec AR</strong> avant toute action en justice. Sans ce recommandé, votre procédure sera <strong>irrecevable</strong>.
                  </p>
                </div>
              </div>
            </div>

            {/* Stat persuasive */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-4 text-white">
              <div className="flex items-center gap-4">
                <div className="text-4xl font-black text-emerald-400">78%</div>
                <div>
                  <p className="font-semibold">des débiteurs règlent après réception du recommandé</p>
                  <p className="text-slate-400 text-xs mt-1">
                    Le recommandé formalise la situation. La plupart préfèrent régler plutôt qu'aller au tribunal.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Clearbus */}
            <div className="border-2 border-emerald-500 rounded-2xl p-5 bg-gradient-to-br from-emerald-50 to-green-50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-bold text-slate-900 text-lg">Recommandé Électronique</h3>
                    <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">RECOMMANDÉ</span>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">
                    Envoi <strong>instantané</strong> avec accusé de réception. Valeur juridique identique au recommandé postal (Décret n°2018-347).
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 px-2 py-1 rounded-full text-emerald-700">
                      <Check className="w-3 h-3" /> Valeur juridique
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 px-2 py-1 rounded-full text-emerald-700">
                      <Check className="w-3 h-3" /> Accusé de réception
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 px-2 py-1 rounded-full text-emerald-700">
                      <Check className="w-3 h-3" /> Envoi en 2 min
                    </span>
                  </div>
                  <button
                    onClick={handleClearbusCheckout}
                    disabled={!formData.debiteur_email}
                    className="w-full bg-emerald-500 text-white py-4 rounded-xl font-bold text-lg hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Send className="w-5 h-5" />
                    Envoyer maintenant — 8,90 €
                  </button>
                  {!formData.debiteur_email && (
                    <p className="text-amber-600 text-sm mt-2 text-center font-medium">⚠️ Renseignez l'email du destinataire à l'étape 1</p>
                  )}
                </div>
              </div>
            </div>

            {/* Télécharger PDF */}
            <div className="border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-slate-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-700 mb-1">Télécharger le PDF</h3>
                  <p className="text-slate-500 text-sm mb-3">
                    Téléchargez la lettre et envoyez-la vous-même en recommandé AR.
                  </p>
                  <button
                    onClick={() => window.print()}
                    className="w-full bg-slate-100 text-slate-700 py-3 rounded-xl font-medium hover:bg-slate-200 flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Télécharger en PDF
                  </button>
                  <p className="text-xs text-amber-600 mt-2 text-center">⚠️ L'envoi doit être fait en recommandé avec AR</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-4 mt-6">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Précédent
            </button>
          )}
          {currentStep < steps.length - 1 && (
            <button
              onClick={() => {
                if (currentStep === 1) generateLetter();
                setCurrentStep(prev => prev + 1);
              }}
              className="flex-1 py-3 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600 flex items-center justify-center gap-2"
            >
              Continuer
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Vue Liste des impayés
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gérer les impayés</h1>
          <p className="text-slate-500 mt-1">Suivez les retards de paiement et envoyez des mises en demeure</p>
        </div>
        <a
          href="/impayes-copropriete"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
        >
          <FileText className="w-4 h-4" />
          Guide complet des impayés
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-red-600">
            {Object.keys(impayesParCopro).length}
          </div>
          <div className="text-sm text-slate-500">Copropriétaires en retard</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-red-600">
            {Object.values(impayesParCopro).reduce((acc, i) => acc + i.total, 0).toLocaleString('fr-FR')} €
          </div>
          <div className="text-sm text-slate-500">Total impayés</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-amber-600">
            {Object.values(impayesParCopro).filter(i => i.appels.some(a => a.relance_niveau > 0)).length}
          </div>
          <div className="text-sm text-slate-500">Relances envoyées</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-emerald-600">
            {coproprietaires.length - Object.keys(impayesParCopro).length}
          </div>
          <div className="text-sm text-slate-500">À jour</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher un copropriétaire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
          />
        </div>
        <div className="flex gap-2">
          {[
            { value: 'all', label: 'Tous' },
            { value: 'impayes', label: 'Impayés' },
            { value: 'relances', label: 'Relancés' },
          ].map((filter) => (
            <button
              key={filter.value}
              onClick={() => setFilterStatus(filter.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === filter.value
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-emerald-300'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Liste */}
      {coproprietairesAvecImpayes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Aucun impayé !</h3>
          <p className="text-slate-500">Tous vos copropriétaires sont à jour de leurs paiements.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {coproprietairesAvecImpayes.map((copro) => (
            <div key={copro.id} className="bg-white rounded-xl border border-slate-200 p-4 hover:border-emerald-300 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{copro.prenom} {copro.nom}</h3>
                    <p className="text-sm text-slate-500">{copro.email || 'Pas d\'email'}</p>
                    {copro.lot && <p className="text-xs text-slate-400">Lot {copro.lot}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-red-600">{copro.impayes.total.toLocaleString('fr-FR')} €</p>
                  <p className="text-xs text-slate-500">{copro.impayes.appels.length} appel(s) impayé(s)</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Échéance : {copro.impayes.derniere_echeance ? new Date(copro.impayes.derniere_echeance).toLocaleDateString('fr-FR') : 'N/A'}
                  </span>
                  {copro.impayes.appels.some(a => a.relance_niveau > 0) && (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Mail className="w-4 h-4" />
                      Relancé
                    </span>
                  )}
                </div>
                <button
                  onClick={() => startMiseEnDemeure(copro)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Mise en demeure
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GestionImpayes;
